import React, { useEffect, useMemo, useState, useCallback } from "react";
import styles from "../styles/popup.module.css";
import Dropdown from "../../../shared/ui/Dropdown/Dropdown";
import { useNavigate } from "react-router-dom";
import { getMe } from "@/entities/user/api/userApi.js";
import { getSystems } from "@/entities/gameSystem/api/gameSystemApi.js";
import { toast } from "react-toastify";
import { FadeIn } from "../../../shared/ui/animations";

import PlayersModal from "./PlayersModal.jsx";
import SeatSelector from "./SeatSelector.jsx";
import DateTimeDropdown from "./DateTimeDropdown";

import {
  checkDayOff,
  getFreeRooms,
  previewPrivateGame,
  createPrivateGame,
  getMasters,
  getWeekendSlots,
} from "../api/formApi";
import Checkbox from "@/shared/ui/Checkbox/index.js";

const DURATION_HOURS = 4;

const ANY_SYSTEM = { id: -1, name: "Мне всё равно" };
const ANY_MASTER = { id: -1, name: "Мне всё равно" };

function toStartAt(dateYYYYMMDD, timeHHmm) {
  return `${dateYYYYMMDD}T${timeHHmm}:00`;
}

export default function Popup({ isOpen, onClose }) {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);

  const [profile, setProfile] = useState(null);
  const [fio, setFio] = useState("");
  const [phone, setPhone] = useState("");

  const [systems, setSystems] = useState([]);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const ANY_OPTION = { id: -1, name: "Мне всё равно" };

  const [masters, setMasters] = useState([ANY_MASTER]);
  const [selectedMaster, setSelectedMaster] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [dayOff, setDayOff] = useState(null);

  const [selectedTime, setSelectedTime] = useState(null);
  const [hasFreeRooms, setHasFreeRooms] = useState(null);

  // выходные: реальные слоты weekend-slots
  const [weekendSlots, setWeekendSlots] = useState([]);
  const [selectedSlotTemplate, setSelectedSlotTemplate] = useState(null);

  const [countPlace, setCountPlace] = useState(4);
  const [friendIds, setFriendIds] = useState([]);
  const [isPlayersModalOpen, setIsPlayersModalOpen] = useState(false);

  const [isNewbies, setIsNewbies] = useState(false);

  const [telegram, setTelegram] = useState("");
  const [comment, setComment] = useState("");
  const [preview, setPreview] = useState(null);
  const [afterPaymentMessage, setAfterPaymentMessage] = useState("");
  const [managerContact, setManagerContact] = useState("");

  useEffect(() => {
    document.body.style.overflowY = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflowY = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setStep(1);

    setSelectedSystem(null);
    setSelectedMaster(null);
    setMasters([ANY_MASTER]);

    setSelectedDate(null);
    setDayOff(null);
    setSelectedTime(null);
    setHasFreeRooms(null);

    setWeekendSlots([]);
    setSelectedSlotTemplate(null);

    setCountPlace(4);
    setFriendIds([]);
    setIsNewbies(false);

    setTelegram("");
    setComment("");
    setPreview(null);

    setAfterPaymentMessage("");
    setManagerContact("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    getMe()
        .then((data) => {
          if (data?.name && data?.surname) {
            setProfile(data);
            setFio(`${data.name} ${data.surname}`);
            setPhone(data.phone || "");
          } else {
            setProfile(null);
            navigate("/auth", { replace: true });
          }
        })
        .catch(() => {
          setProfile(null);
          navigate("/auth", { replace: true });
        });
  }, [isOpen, navigate]);

  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      try {
        const res = await getSystems();
        const list = Array.isArray(res) ? res : res.data;
        setSystems(list || []);
      } catch {
        setSystems([]);
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (selectedSystem === null) {
      setMasters([ANY_MASTER]);
      setSelectedMaster(null);
      return;
    }

    const gameSystemId = selectedSystem.id;

    (async () => {
      try {
        const data = await getMasters(gameSystemId);
        const list = Array.isArray(data) ? data : [];
        const next = [ANY_MASTER, ...list];
        setMasters(next);

        if (selectedMaster && !next.some((m) => m.id === selectedMaster.id)) {
          setSelectedMaster(null);
        }
      } catch (e) {
        console.error(e);
        setMasters([ANY_MASTER]);
        setSelectedMaster(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedSystem?.id]);

  const handlePickDate = async (dateYYYYMMDD) => {
    setSelectedDate(dateYYYYMMDD);

    setDayOff(null);
    setSelectedTime(null);
    setHasFreeRooms(null);

    setWeekendSlots([]);
    setSelectedSlotTemplate(null);

    try {
      const res = await checkDayOff(dateYYYYMMDD);
      const isDayOff = !!res.dayOff;
      setDayOff(isDayOff);

      if (isDayOff) {
        try {
          const slots = await getWeekendSlots(dateYYYYMMDD, dateYYYYMMDD);

          const grouped = (slots || []).reduce((acc, slot) => {
            const key = String(slot.slotTemplateId);

            if (!acc[key]) {
              acc[key] = {
                id: slot.slotTemplateId,
                name: slot.roomName || "Слот",
                startTime: slot.slotStartTime,
                endTime: slot.slotEndTime,
                occupied: !!slot.occupied,
                roomId: slot.roomId ?? null,
                roomCyberpunk: slot.roomCyberpunk ?? null,
                raw: [slot],
              };
            } else {
              acc[key].raw.push(slot);
            }

            return acc;
          }, {});

          const normalizedSlots = Object.values(grouped).map((group) => {
            const hasFree = group.raw.some((item) => !item.occupied);

            return {
              id: group.id,
              name: group.name,
              startTime: group.startTime,
              endTime: group.endTime,
              occupied: !hasFree,
              roomId: group.roomId,
              roomCyberpunk: group.roomCyberpunk,
              items: group.raw,
            };
          });

          normalizedSlots.sort((a, b) => {
            const aTime = a.startTime || "";
            const bTime = b.startTime || "";
            return aTime.localeCompare(bTime);
          });

          setWeekendSlots(normalizedSlots);
        } catch (e) {
          console.error(e);
          toast.error("Не удалось получить слоты выходного дня");
          setWeekendSlots([]);
        }
      } else {
        setWeekendSlots([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Ошибка при определении типа дня");
      setDayOff(null);
    }
  };

  const handlePickTime = async (timeHHmm) => {
    setSelectedTime(timeHHmm);
    setHasFreeRooms(null);

    if (!selectedDate) return;

    try {
      const startAt = toStartAt(selectedDate, timeHHmm);
      const free = await getFreeRooms(startAt, DURATION_HOURS);
      const ok = Array.isArray(free) && free.length > 0;
      setHasFreeRooms(ok);

      if (!ok) {
        toast.error("На выбранное время нет свободных залов");
      }
    } catch (e) {
      console.error(e);
      toast.error("Не удалось получить свободные залы");
      setHasFreeRooms(false);
    }
  };

  const playersValidation = useMemo(() => {
    const required = Math.max(0, countPlace - 1);
    const ids = friendIds.map((x) => Number(x));

    const selfId = profile?.id ? Number(profile.id) : null;
    const hasSelf = selfId !== null ? ids.some((x) => x === selfId) : false;

    const realIds = ids.filter((x) => x > 0);
    const uniqueReal = new Set(realIds);
    const hasDuplicateReal = uniqueReal.size !== realIds.length;

    const enough = ids.length === required;

    return {
      required,
      enough,
      hasSelf,
      hasDuplicateReal,
      ok: enough && !hasSelf && !hasDuplicateReal,
    };
  }, [friendIds, countPlace, profile]);

  const canGoStep2 = useMemo(() => {
    if (!selectedSystem) return false;
    if (!selectedDate) return false;
    if (dayOff === null) return false;

    if (dayOff) {
      if (!selectedSlotTemplate) return false;
      if (selectedSlotTemplate.occupied) return false;
      return true;
    }

    if (!selectedTime) return false;
    if (hasFreeRooms !== true) return false;

    return true;
  }, [selectedSystem, selectedDate, dayOff, selectedSlotTemplate, selectedTime, hasFreeRooms]);

  const getFriendIdsToSend = useCallback(() => {
    const required = Math.max(0, countPlace - 1);

    if (isNewbies) return Array(required).fill(-1);
    if (!friendIds || friendIds.length === 0) return Array(required).fill(-1);

    return friendIds.map((x) => Number(x));
  }, [countPlace, friendIds, isNewbies]);

  const goStep3WithPreview = async () => {
    const required = Math.max(0, countPlace - 1);
    const idsToSend = getFriendIdsToSend();

    const isManualSelection = !isNewbies && (friendIds?.length ?? 0) > 0;

    if (isManualSelection) {
      if (!playersValidation.ok) {
        if (!playersValidation.enough) {
          toast.error(`Нужно указать ровно ${playersValidation.required} игроков (без учёта вас)`);
          return;
        }
        if (playersValidation.hasSelf) {
          toast.error("Нельзя добавлять себя в список игроков");
          return;
        }
        if (playersValidation.hasDuplicateReal) {
          toast.error("Один и тот же пользователь не может быть добавлен дважды");
          return;
        }
        toast.error("Проверь список игроков");
        return;
      }
    } else {
      if (idsToSend.length !== required) {
        toast.error(`Нужно указать ${required} игроков (без учёта вас)`);
        return;
      }
    }

    try {
      const base = {
        gameSystemId: selectedSystem.id,
        startedDate: selectedDate,
        friendIds: idsToSend,
        durationHours: DURATION_HOURS,
        ...(selectedMaster?.id !== -1 ? { masterId: selectedMaster.id } : {}),
      };

      const payload = dayOff
          ? { ...base, slotTemplateId: selectedSlotTemplate.id }
          : { ...base, startedTime: selectedTime };

      const p = await previewPrivateGame(payload);
      setPreview(p);
      setAfterPaymentMessage(p?.afterPaymentMessage || "");
      setManagerContact(p?.managerContact || "");
      setStep(3);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Ошибка preview");
    }
  };

  const handlePay = async () => {
    if (!telegram?.trim()) {
      toast.error("Укажи ник Telegram");
      return;
    }

    const required = Math.max(0, countPlace - 1);
    const idsToSend = getFriendIdsToSend();
    const isManualSelection = !isNewbies && (friendIds?.length ?? 0) > 0;

    if (isManualSelection) {
      if (!playersValidation.ok) {
        toast.error("Сначала исправь список игроков (шаг 2)");
        return;
      }
    } else {
      if (idsToSend.length !== required) {
        toast.error(`Нужно указать ${required} игроков (без учёта вас)`);
        return;
      }
    }

    try {
      const base = {
        gameSystemId: selectedSystem.id,
        startedDate: selectedDate,
        telegram: telegram.trim(),
        friendIds: idsToSend,
        description: comment || "",
        ...(selectedMaster?.id !== -1 ? { masterId: selectedMaster.id } : {}),
      };

      const payload = dayOff
          ? { ...base, slotTemplateId: selectedSlotTemplate.id }
          : { ...base, startedTime: selectedTime };

      const res = await createPrivateGame(payload);

      if (res?.afterPaymentMessage) setAfterPaymentMessage(res.afterPaymentMessage);
      if (res?.managerContact) setManagerContact(res.managerContact);

      if (res?.paymentUrl) {
        window.location.href = res.paymentUrl;
      } else {
        toast.success("Заявка отправлена");
        onClose();
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Ошибка создания заявки");
    }
  };

  const handlePlaceCountChange = useCallback(
      (cnt) => {
        const n = Number(cnt);
        if (!n) return;

        if (n < 4) {
          setCountPlace(4);
          setFriendIds((prev) => prev.slice(0, 3));
          return;
        }

        setCountPlace(n);

        if (isNewbies) {
          setFriendIds(Array(Math.max(0, n - 1)).fill(-1));
          return;
        }

        setFriendIds((prev) => prev.slice(0, Math.max(0, n - 1)));
      },
      [isNewbies]
  );

  const toggleNewbies = useCallback(() => {
    setIsNewbies((prev) => {
      const next = !prev;

      if (next) {
        setFriendIds(Array(Math.max(0, countPlace - 1)).fill(-1));
      } else {
        setFriendIds([]);
      }

      return next;
    });
  }, [countPlace]);

  if (!isOpen) return null;

  return (
      <div className={styles.popupOverlay} onClick={onClose}>
        <FadeIn>
          <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
            {step > 1 && step <= 3 && (
                <button className={styles.btnBack} onClick={() => setStep((p) => p - 1)}>
                  <img src="/images/backArrow.svg" alt="Назад" />
                </button>
            )}

            <button className={styles.popupClose} onClick={onClose}>
              <img src="/images/cross.svg" alt="закрыть" />
            </button>

            <div className={styles.optionsWrapper}>
              <div className="text-center mb-2">
                <h2 className="mb-2">ЗАПИСАТЬСЯ НА ЗАКРЫТУЮ ИГРУ</h2>
                <div style={{ opacity: 0.85 }}>ШАГ {step}</div>
              </div>

              {step === 1 && (
                  <>
                    <Dropdown
                        options={[ANY_OPTION, ...systems]}
                        selected={selectedSystem}
                        onSelect={(s) => {
                          setSelectedSystem(s);
                          setSelectedMaster(null);
                        }}
                        getLabel={(s) => s.name}
                        name="system"
                        placeholder="Выберите систему"
                        className={styles.dropdown}
                    />

                    <Dropdown
                        options={masters}
                        selected={selectedMaster}
                        onSelect={setSelectedMaster}
                        getLabel={(m) => m.name}
                        name="master"
                        placeholder="Выберите мастера"
                        className={styles.dropdown}
                        disabled={!selectedSystem}
                    />

                    <DateTimeDropdown
                        value={selectedDate}
                        onChange={handlePickDate}
                        placeholder="Дата"
                        mode="date"
                    />

                    {dayOff === false && (
                        <DateTimeDropdown
                            value={selectedTime}
                            onChange={handlePickTime}
                            placeholder="Время"
                            mode="time"
                            timeStep={30}
                        />
                    )}

                    {dayOff === true && (
                        <div>
                          <div className={`body capital ${styles.slotTitle}`}>СЛОТЫ</div>

                          <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                                gap: 20,
                              }}
                          >
                            {weekendSlots.map((t) => {
                              const active = selectedSlotTemplate?.id === t.id;
                              const disabled = t.occupied;

                              return (
                                  <button
                                      key={t.id}
                                      type="button"
                                      onClick={() => !disabled && setSelectedSlotTemplate(t)}
                                      className={styles.slotBtn}
                                      disabled={disabled}
                                      style={{
                                        color: active ? "#16120F" : "var(--fill-green)",
                                        background: active ? "var(--fill-green)" : "transparent",
                                        opacity: disabled ? 0.5 : 1,
                                        cursor: disabled ? "not-allowed" : "pointer",
                                      }}
                                  >
                                    <div className={`body ${styles.slotName}`}>
                                      {"Слот"}
                                    </div>

                                    <div className="body-bold">
                                      {t.startTime ? String(t.startTime).slice(0, 5) : "--:--"}
                                    </div>
                                  </button>
                              );
                            })}
                          </div>

                          <div style={{ marginTop: 18, opacity: 0.9 }}>
                            Цена игры в ночной слот - 2600 руб за место
                          </div>
                        </div>
                    )}

                    <button
                        type="button"
                        className="btn btn-brown-noLine w-100"
                        disabled={!canGoStep2}
                        onClick={() => setStep(2)}
                        style={{ marginTop: 20 }}
                    >
                      ДАЛЕЕ
                    </button>
                  </>
              )}

              {step === 2 && (
                  <>
                    <div className="text-center mb-3" style={{ opacity: 0.9 }}>
                      Минимальное количество оплаченных мест для Закрытых игр - 4
                    </div>
                    <div className="text-center mb-3" style={{ opacity: 0.9 }}>
                      Через кнопку “Добавить игроков” вы можете указать сопартийцев, которые
                      зарегистрированы на сайте для начисления бонусов
                    </div>

                    <SeatSelector
                        scheduleId={null}
                        maxSeats={6}
                        takenSeats={0}
                        label="Кол-во мест"
                        initialCount={4}
                        onChange={handlePlaceCountChange}
                    />

                    <Checkbox
                        checked={isNewbies}
                        label="Мы новички"
                        onChange={() => {
                          toggleNewbies();
                        }}
                    />

                    <button
                        type="button"
                        className="btn btn-green-outline w-100"
                        onClick={() => setIsPlayersModalOpen(true)}
                        style={{ marginTop: 12 }}
                        disabled={isNewbies}
                    >
                      ДОБАВИТЬ ИГРОКОВ
                    </button>

                    <button
                        type="button"
                        className="btn btn-brown-noLine w-100"
                        onClick={goStep3WithPreview}
                        style={{ marginTop: 16 }}
                    >
                      ДАЛЕЕ
                    </button>
                  </>
              )}

              {step === 3 && (
                  <>
                    <input className="input" value={fio} readOnly placeholder="Имя" />
                    <input className="input" value={phone} readOnly placeholder="Номер телефона" />

                    <input
                        type="text"
                        className="input"
                        placeholder="Ник телеграм"
                        value={telegram}
                        onChange={(e) => setTelegram(e.target.value)}
                        required
                    />

                    <textarea
                        className={styles.textarea}
                        placeholder="Комментарий"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                    />

                    <div style={{ color: "var(--fill-green)", margin: "10px 0" }}>
                      К ПРЕДОПЛАТЕ: {preview?.prepaymentAmount ?? "..."} ₽
                    </div>

                    {preview?.gameRoomName && (
                        <div style={{ opacity: 0.85, marginBottom: 10 }}>
                          <span style={{ opacity: 0.75 }}>Зал: </span>
                          <span>{preview.gameRoomName}</span>
                        </div>
                    )}

                    {(afterPaymentMessage || managerContact) && (
                        <div
                            style={{
                              border: "1px solid var(--fill-green)",
                              padding: "12px",
                              margin: "12px 0",
                              color: "var(--font-green)",
                            }}
                        >
                          {afterPaymentMessage && (
                              <div style={{ marginBottom: managerContact ? 8 : 0 }}>
                                {afterPaymentMessage}
                              </div>
                          )}

                          {managerContact && (
                              <div>
                                <span style={{ opacity: 0.8 }}>Контакт менеджера: </span>
                                <span>{managerContact}</span>
                              </div>
                          )}
                        </div>
                    )}

                    <button type="button" className="btn btn-brown-noLine w-100" onClick={handlePay}>
                      ОПЛАТИТЬ
                    </button>
                  </>
              )}
            </div>

            <PlayersModal
                isOpen={isPlayersModalOpen}
                onClose={() => setIsPlayersModalOpen(false)}
                onSubmit={(ids) => {
                  setFriendIds(ids);
                  setIsPlayersModalOpen(false);
                  setIsNewbies(false);
                }}
                heroIds={friendIds}
                setHeroIds={setFriendIds}
                isCyber={true}
                isPrivateGame={false}
                propMaxSeats={countPlace}
                propTakenSeats={0}
            />
          </div>
        </FadeIn>
      </div>
  );
}